// User Feature Role Assignment Service
// Handles assigning and managing feature roles for users

const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface UserFeatureRole {
  role_id: string;
  name: string;
  description: string;
  assigned_at: string;
  assigned_by: number;
}

export interface AvailableFeatureRole {
  id: string;
  name: string;
  description: string;
}

export interface AssignRoleRequest {
  roleId: string;
}

class UserFeatureRoleService {
  // Get user's currently assigned feature roles
  async getUserFeatureRoles(userId: string): Promise<UserFeatureRole[]> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/roles/users/${userId}/feature-roles`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get user feature roles');
    }

    const result = await response.json();
    return result.data;
  }

  // Get all available feature roles for assignment
  async getAvailableFeatureRoles(): Promise<AvailableFeatureRole[]> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/roles/available-feature-roles`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get available feature roles');
    }

    const result = await response.json();
    return result.data;
  }

  // Assign a feature role to a user
  async assignFeatureRoleToUser(userId: string, roleId: string): Promise<void> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/roles/users/${userId}/feature-roles`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ roleId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to assign feature role');
    }
  }

  // Remove a feature role from a user
  async removeFeatureRoleFromUser(userId: string, roleId: string): Promise<void> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/roles/users/${userId}/feature-roles`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ roleId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to remove feature role');
    }
  }
}

export default new UserFeatureRoleService();